import React, { useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import { EventTypeSelector } from 'components/RemindersGadget';
import RemindersGadgetContent from 'components/RemindersGadget/RemindersGadgetContent';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';

import buildRemindersDefinition from './buildRemindersDefinition';
import { CardShell } from './CardChrome';
import ChartPlaceholder from './ChartPlaceholder';
import LegacyGadgetBody from './LegacyGadgetBody';
import StudioContentTypeContext from './StudioContentTypeContext';

const SelectorRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 8px 4px;
`;

// On a dashboard or report the picker takes no row: as on the classic
// gadget, it's revealed while the block is hovered (or its menu is open),
// floated over the top-right corner
const HoverSelector = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  right: 8px;
  z-index: 1;
  background-color: var(--color-bg-raised);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? 'auto' : 'none')};
  transition: opacity 0.15s ease;

  ${CardShell}:hover &,
  &:focus-within {
    opacity: 1;
    pointer-events: auto;
  }
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

// A reminders Table (a block, or a Composite's element): drawn by the legacy
// Reminders gadget it saves as, within its dates and filters. Viewers can
// narrow it to some of its events, as on the classic gadget. Stable while its
// settings don't change, so it doesn't refetch on a move. w is the grid width
// of the block it sits in.
const StudioReminders = ({
  content,
  name,
  w,
  isInteractive,
  isOnView = false,
}: {
  content: Studio.ChartContent;
  name: string;
  w: number;
  isInteractive: boolean;
  // On a dashboard or report, rather than in Studio
  isOnView?: boolean;
}) => {
  const contentType = useContext(StudioContentTypeContext);
  const events = content.table?.reminders?.events;
  const eventsKey = JSON.stringify(events ?? []);
  const built = useMemo(
    () =>
      buildRemindersDefinition({
        card: {
          visType: 'table',
          description: content.description,
          table: {
            rows: 'reminders',
            reminders: { events: JSON.parse(eventsKey) },
          },
        },
        id: content.id,
        name,
        isReport: contentType === 'report',
      }),
    // The events are compared by value
    [content.description, content.id, contentType, eventsKey, name],
  );
  const gadget = 'chartDefinition' in built ? built.chartDefinition : undefined;
  const [allowedEvents, setAllowedEvents] = useState<string[]>(
    gadget ? gadget.events : [],
  );
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  // Events taken off the block leave the viewer's selection too
  useEffect(() => {
    setEventTypes((current) =>
      _.intersection(current, JSON.parse(eventsKey) as string[]),
    );
  }, [eventsKey]);

  if (!gadget) {
    return (
      <ChartPlaceholder
        cardId={content.id}
        visType="table"
        message={'missing' in built ? built.missing : ''}
      />
    );
  }
  return (
    <LegacyGadgetBody
      $isInteractive={isInteractive}
      data-testid={`studio-card-reminders-${content.id}`}
    >
      <div>
        {gadget.events.length > 1 &&
          (isOnView ? (
            <HoverSelector
              $isOpen={isSelectorOpen}
              data-testid={`studio-reminders-type-${content.id}`}
            >
              <EventTypeSelector
                allowedEvents={gadget.events}
                eventTypes={eventTypes}
                setEventTypes={setEventTypes}
                isSmall={w < 4}
                onOpened={() => setIsSelectorOpen(true)}
                onClosed={() => setIsSelectorOpen(false)}
              />
            </HoverSelector>
          ) : (
            <SelectorRow>
              <EventTypeSelector
                allowedEvents={gadget.events}
                eventTypes={eventTypes}
                setEventTypes={setEventTypes}
                isSmall={w < 4}
                onOpened={_.noop}
                onClosed={_.noop}
              />
            </SelectorRow>
          ))}
        <Content>
          <GqlClientProvider
            visName={gadget.name}
            visId={gadget.id}
            visType={gadget.type}
          >
            <RemindersGadgetContent
              gadget={gadget}
              allowedEvents={allowedEvents}
              setAllowedEvents={setAllowedEvents}
              eventTypes={eventTypes}
            />
          </GqlClientProvider>
        </Content>
      </div>
    </LegacyGadgetBody>
  );
};

export default StudioReminders;
